import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Upload,
  Download,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from "lucide-react";
import Papa from "papaparse";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leagueId: string;
  organizationId: string;
}

interface ParsedRow {
  rowNum: number;
  firstName1: string;
  lastName1: string;
  email1: string;
  phone1: string;
  level1: number | null;
  firstName2: string;
  lastName2: string;
  email2: string;
  phone2: string;
  level2: number | null;
  teamName: string;
  availableDays: string;
  unavailableDays: string;
  playWomens: boolean;
  playMens: boolean;
  notes: string;
  player1Status: "existing" | "new" | "error";
  player2Status: "existing" | "new" | "error";
  player1Id: string | null;
  player2Id: string | null;
  customer1Id: string | null;
  customer2Id: string | null;
  errors: string[];
  warnings: string[];
}

type Step = 1 | 2 | 3;

const TEMPLATE_HEADERS = [
  "Förnamn 1",
  "Efternamn 1",
  "Mail 1",
  "Telefon 1",
  "Nivå 1",
  "Förnamn 2",
  "Efternamn 2",
  "Mail 2",
  "Telefon 2",
  "Nivå 2",
  "Lagnamn",
  "Tillgängliga dagar",
  "Ej tillgängliga dagar",
  "Möter damer",
  "Möter herrar",
  "Övrigt",
];

const TEMPLATE_ROWS = [
  [
    "Rickard",
    "Eliasson",
    "rickard@example.se",
    "0731234567",
    "7.5",
    "Henrik",
    "Svensson",
    "henrik@example.se",
    "0739876543",
    "6.0",
    "",
    "Mån,Tis,Tor",
    "Ons",
    "Ja",
    "Ja",
    "Vill helst spela kvällar",
  ],
  [
    "Anna",
    "Karlsson",
    "anna@example.se",
    "0701112222",
    "4.5",
    "Eva",
    "Lindgren",
    "eva@example.se",
    "0702223333",
    "5.0",
    "Team AE",
    "Alla",
    "",
    "Nej",
    "Ja",
    "Kan ej v.22",
  ],
];

function downloadTemplate() {
  const lines = [
    TEMPLATE_HEADERS.join(";"),
    ...TEMPLATE_ROWS.map((r) => r.join(";")),
  ];
  const csv = lines.join("\n");
  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "lag_import_mall.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function parseLevel(val: string): number | null {
  if (!val || val.trim() === "") return null;
  const n = parseFloat(val.replace(",", "."));
  if (isNaN(n) || n < 1.0 || n > 10.0) return null;
  return n;
}

function parseBool(val: string): boolean {
  const v = (val || "").trim().toLowerCase();
  return v === "ja" || v === "yes" || v === "1" || v === "true";
}

export default function LeagueCSVImport({
  open,
  onOpenChange,
  leagueId,
  organizationId,
}: Props) {
  const { t } = useTranslation("admin");
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>(1);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<{
    success: number;
    failed: number;
  } | null>(null);

  function reset() {
    setStep(1);
    setRows([]);
    setImporting(false);
    setProgress(0);
    setImportResult(null);
  }

  function handleClose(openState: boolean) {
    if (!openState) reset();
    onOpenChange(openState);
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      delimiter: ";",
      header: true,
      skipEmptyLines: true,
      complete: async (result) => {
        if (result.errors.length > 0 && result.data.length === 0) {
          toast.error(t("leagues.import.couldNotReadCsv"));
          return;
        }
        await processRows(result.data as Record<string, string>[]);
      },
      error: () => {
        toast.error(t("leagues.import.couldNotReadFile"));
      },
    });

    // Reset file input so same file can be re-selected
    e.target.value = "";
  }

  async function processRows(data: Record<string, string>[]) {
    // Collect all emails for batch lookup
    const allEmails = [
      ...new Set(
        data
          .flatMap((r) => [r["Mail 1"], r["Mail 2"]])
          .filter(Boolean)
          .map((e) => e.toLowerCase())
      ),
    ];

    const { data: existingCustomers } = await supabase
      .from("customers")
      .select("id, user_id, email, full_name, level")
      .eq("organization_id", organizationId)
      .in("email", allEmails);

    const customerMap = new Map(
      existingCustomers?.map((c) => [c.email.toLowerCase(), c]) ?? []
    );

    // Track email usage across rows for duplicate warnings
    const emailUsage = new Map<string, number[]>();

    const parsed: ParsedRow[] = data.map((r, idx) => {
      const rowNum = idx + 1;
      const errors: string[] = [];
      const warnings: string[] = [];

      const firstName1 = (r["Förnamn 1"] || "").trim();
      const lastName1 = (r["Efternamn 1"] || "").trim();
      const email1 = (r["Mail 1"] || "").trim();
      const phone1 = (r["Telefon 1"] || "").trim();
      const level1Raw = (r["Nivå 1"] || "").trim();
      const firstName2 = (r["Förnamn 2"] || "").trim();
      const lastName2 = (r["Efternamn 2"] || "").trim();
      const email2 = (r["Mail 2"] || "").trim();
      const phone2 = (r["Telefon 2"] || "").trim();
      const level2Raw = (r["Nivå 2"] || "").trim();
      const teamName = (r["Lagnamn"] || "").trim();
      const availableDays = (r["Tillgängliga dagar"] || "").trim();
      const unavailableDays = (r["Ej tillgängliga dagar"] || "").trim();
      const playWomens = parseBool(r["Möter damer"]);
      const playMens = parseBool(r["Möter herrar"]);
      const notes = (r["Övrigt"] || "").trim();

      // Required fields
      if (!firstName1) errors.push("Förnamn 1 saknas");
      if (!lastName1) errors.push("Efternamn 1 saknas");
      if (!email1) errors.push("Mail 1 saknas");
      if (!firstName2) errors.push("Förnamn 2 saknas");
      if (!lastName2) errors.push("Efternamn 2 saknas");
      if (!email2) errors.push("Mail 2 saknas");

      // Email duplicate within row
      if (
        email1 &&
        email2 &&
        email1.toLowerCase() === email2.toLowerCase()
      ) {
        errors.push("Spelare 1 och 2 har samma mail");
      }

      // Level validation
      let level1: number | null = null;
      if (level1Raw) {
        level1 = parseLevel(level1Raw);
        if (level1 === null) errors.push("Ogiltig nivå för spelare 1");
      }
      let level2: number | null = null;
      if (level2Raw) {
        level2 = parseLevel(level2Raw);
        if (level2 === null) errors.push("Ogiltig nivå för spelare 2");
      }

      // Track emails for cross-row duplicate detection
      if (email1) {
        const key = email1.toLowerCase();
        emailUsage.set(key, [...(emailUsage.get(key) || []), rowNum]);
      }
      if (email2) {
        const key = email2.toLowerCase();
        emailUsage.set(key, [...(emailUsage.get(key) || []), rowNum]);
      }

      // Customer lookup
      const customer1 = email1
        ? customerMap.get(email1.toLowerCase())
        : undefined;
      const customer2 = email2
        ? customerMap.get(email2.toLowerCase())
        : undefined;

      const player1Status: "existing" | "new" | "error" =
        errors.length > 0 ? "error" : customer1 ? "existing" : "new";
      const player2Status: "existing" | "new" | "error" =
        errors.length > 0 ? "error" : customer2 ? "existing" : "new";

      return {
        rowNum,
        firstName1,
        lastName1,
        email1,
        phone1,
        level1,
        firstName2,
        lastName2,
        email2,
        phone2,
        level2,
        teamName,
        availableDays,
        unavailableDays,
        playWomens,
        playMens,
        notes,
        player1Status,
        player2Status,
        player1Id: customer1?.user_id ?? null,
        player2Id: customer2?.user_id ?? null,
        customer1Id: customer1?.id ?? null,
        customer2Id: customer2?.id ?? null,
        errors,
        warnings,
      };
    });

    // Add cross-row duplicate warnings
    for (const [email, rowNums] of emailUsage.entries()) {
      if (rowNums.length > 1) {
        for (const rowNum of rowNums) {
          const row = parsed.find((r) => r.rowNum === rowNum);
          if (row) {
            row.warnings.push(
              `${email} finns i flera rader (${rowNums.join(", ")})`
            );
          }
        }
      }
    }

    setRows(parsed);
    setStep(2);
  }

  async function handleImport() {
    setStep(3);
    setImporting(true);
    setProgress(0);

    const validRows = rows.filter((r) => r.errors.length === 0);
    let success = 0;
    let failed = 0;

    // Rebuild customer map for import
    const allEmails = [
      ...new Set(
        validRows
          .flatMap((r) => [r.email1, r.email2])
          .filter(Boolean)
          .map((e) => e.toLowerCase())
      ),
    ];
    const { data: existingCustomers } = await supabase
      .from("customers")
      .select("id, user_id, email, full_name, level")
      .eq("organization_id", organizationId)
      .in("email", allEmails);

    const customerMap = new Map(
      existingCustomers?.map((c) => [c.email.toLowerCase(), c]) ?? []
    );

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];

      try {
        // TASK-67 PR-D: resolve each row's identity into either a
        // registered user_id or an unregistered customer_id (mutually
        // exclusive — team_members_identity_check enforces this). The
        // pre-PR-D code coalesced into a single playerNId variable that
        // doubled as the player1/2_id slot value; that no longer makes
        // sense with player1/2 columns dropped in PR-E.
        type IdentityRef = { userId: string | null; customerId: string | null };

        async function resolveIdentity(
          email: string,
          firstName: string,
          lastName: string,
          phone: string,
          level: number | null,
        ): Promise<IdentityRef> {
          const existing = customerMap.get(email.toLowerCase());
          if (existing?.user_id) {
            return { userId: existing.user_id, customerId: null };
          }
          if (existing) {
            return { userId: null, customerId: existing.id };
          }
          const { data: newCustomer } = await supabase
            .from("customers")
            .insert({
              organization_id: organizationId,
              full_name: `${firstName} ${lastName}`,
              email: email.toLowerCase(),
              phone: phone || null,
              level,
              source: "league_import" as any,
            } as any)
            .select("id, user_id")
            .single();
          if (newCustomer) {
            customerMap.set(email.toLowerCase(), {
              id: newCustomer.id,
              user_id: newCustomer.user_id,
              email: email.toLowerCase(),
              full_name: `${firstName} ${lastName}`,
              level,
            } as any);
            return newCustomer.user_id
              ? { userId: newCustomer.user_id, customerId: null }
              : { userId: null, customerId: newCustomer.id };
          }
          return { userId: null, customerId: null };
        }

        const id1 = await resolveIdentity(
          row.email1, row.firstName1, row.lastName1, row.phone1, row.level1,
        );
        const id2 = await resolveIdentity(
          row.email2, row.firstName2, row.lastName2, row.phone2, row.level2,
        );

        // Create team — TASK-67 PR-D: dropped player1_id / player2_id
        // from the INSERT (PR-E drops the columns). Membership now lives
        // exclusively in team_members; the INSERT below adds both
        // primaries with the resolved identity.
        const teamName =
          row.teamName || `${row.lastName1} / ${row.lastName2}`;
        const { data: teamRaw } = await supabase
          .from("teams" as any)
          .insert({
            league_id: leagueId,
            team_name: teamName,
            payment_status: "unpaid",
          } as any)
          .select("id")
          .single();
        const team = teamRaw as unknown as { id: string } | null;

        // TASK-67 PR-D: insert team_members rows for both primaries.
        // Each row carries EITHER user_id OR customer_id per the
        // team_members_identity_check CHECK constraint.
        if (team) {
          const memberRows: Array<{
            team_id: string;
            user_id?: string;
            customer_id?: string;
            role: "primary";
          }> = [];
          for (const id of [id1, id2]) {
            if (id.userId) {
              memberRows.push({ team_id: team.id, user_id: id.userId, role: "primary" });
            } else if (id.customerId) {
              memberRows.push({ team_id: team.id, customer_id: id.customerId, role: "primary" });
            }
          }
          if (memberRows.length > 0) {
            await supabase.from("team_members").insert(memberRows as never);
          }
        }

        // Create preferences if any preference fields filled
        if (
          team &&
          (row.availableDays ||
            row.unavailableDays ||
            row.notes ||
            !row.playWomens ||
            !row.playMens)
        ) {
          const parseDays = (s: string) =>
            s
              ? s
                  .split(",")
                  .map((d) => d.trim().toLowerCase())
                  .filter(Boolean)
              : null;

          await supabase.from("league_team_preferences" as any).insert({
            team_id: team.id,
            league_id: leagueId,
            available_days: parseDays(row.availableDays),
            unavailable_days: parseDays(row.unavailableDays),
            will_play_womens: row.playWomens,
            will_play_mens: row.playMens,
            notes: row.notes || null,
          } as any);
        }

        success++;
      } catch (err) {
        console.error(`Failed to import row ${row.rowNum}:`, err);
        failed++;
      }

      setProgress(Math.round(((i + 1) / validRows.length) * 100));
    }

    setImporting(false);
    setImportResult({ success, failed });

    // Invalidate relevant query caches
    await queryClient.invalidateQueries({ queryKey: ["teams"] });
    await queryClient.invalidateQueries({ queryKey: ["league"] });
    await queryClient.invalidateQueries({ queryKey: ["customers"] });

    if (failed === 0) {
      toast.success(t("leagues.import.teamsImported", { count: success }));
    } else {
      toast.warning(t("leagues.import.teamsImportedWithFailures", { success, failed }));
    }
  }

  const validCount = rows.filter((r) => r.errors.length === 0).length;
  const warningCount = rows.filter(
    (r) => r.warnings.length > 0 && r.errors.length === 0
  ).length;
  const errorCount = rows.filter((r) => r.errors.length > 0).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t("leagues.import.title")}</DialogTitle>
        </DialogHeader>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="flex flex-col items-center gap-6 py-8">
            <div className="flex flex-col items-center gap-2 text-center">
              <Upload className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {t("leagues.import.uploadDescription")}
              </p>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" asChild>
                <label className="cursor-pointer">
                  <Upload className="mr-2 h-4 w-4" />
                  {t("leagues.import.chooseFile")}
                  <input
                    type="file"
                    accept=".csv,.txt"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </label>
              </Button>
              <Button variant="ghost" onClick={downloadTemplate}>
                <Download className="mr-2 h-4 w-4" />
                {t("leagues.import.downloadTemplate")}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 2 && (
          <>
            <div className="text-sm text-muted-foreground mb-2">
              {validCount > 0 && <span className="mr-3">{t("leagues.import.teamsOk", { count: validCount })}</span>}
              {warningCount > 0 && (
                <span className="text-orange-500 mr-3">
                  {t("leagues.import.warnings", { count: warningCount })}
                </span>
              )}
              {errorCount > 0 && (
                <span className="text-destructive">
                  {t("leagues.import.errors", { count: errorCount })}
                </span>
              )}
            </div>

            <ScrollArea className="flex-1 min-h-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("leagues.import.row")}</TableHead>
                    <TableHead>{t("leagues.import.team")}</TableHead>
                    <TableHead>{t("leagues.import.player1")}</TableHead>
                    <TableHead>{t("leagues.import.player2")}</TableHead>
                    <TableHead>{t("leagues.import.status")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow
                      key={r.rowNum}
                      className={
                        r.errors.length > 0 ? "bg-destructive/5" : ""
                      }
                    >
                      <TableCell>{r.rowNum}</TableCell>
                      <TableCell className="font-medium">
                        {r.teamName ||
                          `${r.lastName1} / ${r.lastName2}`}
                      </TableCell>
                      <TableCell>
                        {r.firstName1} {r.lastName1}
                        {r.player1Status === "existing" ? (
                          <Badge
                            className="ml-1 text-[10px]"
                            variant="secondary"
                          >
                            {t("leagues.import.existing")}
                          </Badge>
                        ) : (
                          <Badge className="ml-1 text-[10px]">{t("leagues.import.new")}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.firstName2} {r.lastName2}
                        {r.player2Status === "existing" ? (
                          <Badge
                            className="ml-1 text-[10px]"
                            variant="secondary"
                          >
                            {t("leagues.import.existing")}
                          </Badge>
                        ) : (
                          <Badge className="ml-1 text-[10px]">{t("leagues.import.new")}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.errors.length > 0 && (
                          <span className="text-destructive text-xs">
                            {r.errors[0]}
                          </span>
                        )}
                        {r.errors.length === 0 &&
                          r.warnings.length > 0 && (
                            <span className="text-orange-500 text-xs">
                              {r.warnings[0]}
                            </span>
                          )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="ghost" onClick={reset}>
                {t("leagues.import.back")}
              </Button>
              <Button
                onClick={handleImport}
                disabled={validCount === 0}
              >
                {t("leagues.import.importTeams", { count: validCount })}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Step 3: Import progress / result */}
        {step === 3 && (
          <div className="flex flex-col items-center gap-6 py-8">
            {importing ? (
              <>
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">
                  {t("leagues.import.importing")}
                </p>
                <Progress value={progress} className="w-full max-w-xs" />
                <p className="text-xs text-muted-foreground">
                  {progress}%
                </p>
              </>
            ) : importResult ? (
              <>
                {importResult.failed === 0 ? (
                  <CheckCircle2 className="h-10 w-10 text-green-500" />
                ) : (
                  <AlertCircle className="h-10 w-10 text-orange-500" />
                )}
                <div className="text-center">
                  <p className="font-medium">{t("leagues.import.importDone")}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("leagues.import.teamsImported", { count: importResult.success })}
                    {importResult.failed > 0 &&
                      `, ${t("leagues.import.failed", { count: importResult.failed })}`}
                  </p>
                </div>
                <Button onClick={() => handleClose(false)}>{t("leagues.import.close")}</Button>
              </>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
